<div class="workspaces-page">
  <div class="dashboard-header">
    <div>
      <div class="header-title">Workspaces</div>
      <p class="description">Create, select, and manage your workspaces.</p>
    </div>
    <button mat-raised-button color="primary" (click)="newWorkspace()">
      <div class="add-icon-button">
        <ng-icon name="HeroPlusSolid" class="add-icon"></ng-icon>
        New workspace
      </div>
    </button>
  </div>

  @if (loading) {
    <app-loading-overlay></app-loading-overlay>
  }

  @if (!loading && workspaces && workspaces.length > 0) {
    <div class="workspaces-toolbar">
      <mat-form-field
        appearance="outline"
        class="workspaces-search"
        [class.has-value]="searchText"
      >
        <ng-icon
          name="HeroSearch"
          matPrefix
          class="icon-fw search-icon"
          aria-hidden="true"
        ></ng-icon>
        @if (searchText) {
          <ng-icon
            name="HeroX"
            matSuffix
            class="icon-fw mouse-pointer clear-icon"
            aria-label="Clear search"
            (click)="clearSearch()"
          ></ng-icon>
        }
        <input
          matInput
          type="text"
          placeholder="Search workspaces"
          aria-label="Search workspaces"
          [ngModel]="searchText"
          (ngModelChange)="onSearchChange($event)"
        />
      </mat-form-field>
    </div>

    <!--Recent workspaces-->
    @if (!searchText && recentWorkspaces.length > 0) {
      <section class="workspaces-section recent-section">
        <h3 class="workspaces-section-title">Recent</h3>
        <div class="recent-rail">
          @for (workspace of recentWorkspaces; track workspace.uuid) {
            <div
              class="recent-chip"
              tabindex="0"
              role="button"
              [attr.aria-label]="'Open ' + workspace.label"
              (click)="activate(workspace)"
              (keydown.enter)="activate(workspace)"
              (keydown.space)="$event.preventDefault(); activate(workspace)"
            >
              <app-workspace-avatar
                size="recent"
                [workspace]="workspace"
                [initials]="getInitials(workspace)"
                [fallbackClass]="'recent-chip-avatar ' + getAvatarClass(workspace)"
              />
              <span class="recent-chip-body">
                <span class="recent-chip-name">{{ workspace.label }}</span>
                <span
                  class="recent-chip-meta"
                >
                  Opened {{ getLastVisitedLabel(workspace) }}
                </span>
              </span>
              <button
                type="button"
                class="recent-chip-remove"
                [attr.aria-label]="
                  'Remove ' + workspace.label + ' from recents'
                "
                matTooltip="Remove from recents"
                (click)="removeRecent(workspace, $event)"
              >
                <ng-icon name="HeroX" aria-hidden="true"></ng-icon>
              </button>
            </div>
          }
        </div>
      </section>
    }

    <!--My Workspaces-->
    @if (filteredMyWorkspaces.length > 0) {
      <section class="workspaces-section">
        <h3 class="workspaces-section-title">My Workspaces</h3>
        <div class="workspaces-grid">
          @for (workspace of filteredMyWorkspaces; track workspace.uuid) {
            <article
              class="workspace-card"
              tabindex="0"
              role="button"
              [attr.aria-label]="'Open ' + workspace.label"
              (click)="activate(workspace)"
              (keydown.enter)="activate(workspace)"
              (keydown.space)="$event.preventDefault(); activate(workspace)"
            >
              <app-workspace-avatar
                [workspace]="workspace"
                [initials]="getInitials(workspace)"
                [fallbackClass]="'workspace-avatar ' + getAvatarClass(workspace)"
              />

              <div class="workspace-card-body">
                <h4
                  class="workspace-card-name"
                  [matTooltip]="
                    workspace.label +
                    (getOwnerEmail(workspace)
                      ? ' — Owner: ' + getOwnerEmail(workspace)
                      : '')
                  "
                >
                  {{ workspace.label }}
                </h4>
                @if (
                  getDatasourceCount(workspace) !== null ||
                  getModelCount(workspace) !== null
                ) {
                  <div class="workspace-card-stats">
                    @if (getDatasourceCount(workspace) !== null) {
                      <span class="workspace-card-stat" matTooltip="Data Sources">
                        <ng-icon name="HeroDocument" class="icon-fw"></ng-icon>
                        {{ getDatasourceCount(workspace) }}
                      </span>
                    }
                    @if (getModelCount(workspace) !== null) {
                      <span class="workspace-card-stat" matTooltip="Models">
                        <ng-icon name="TablerCategory2" class="icon-fw"></ng-icon>
                        {{ getModelCount(workspace) }}
                      </span>
                    }
                  </div>
                }
                @if (getMostRecentApiRequestLabel(workspace); as lastApiRequest) {
                  <p
                    class="workspace-card-recent"
                    [matTooltip]="getMostRecentApiRequestTooltip(workspace)"
                  >
                    <ng-icon name="HeroClock" class="icon-fw"></ng-icon>
                    {{ lastApiRequest }}
                  </p>
                }
              </div>

              <button
                type="button"
                class="favorite-toggle"
                [class.is-favorite]="isFavorite(workspace)"
                [attr.aria-pressed]="isFavorite(workspace)"
                [attr.aria-label]="
                  isFavorite(workspace)
                    ? 'Unpin ' + workspace.label
                    : 'Pin ' + workspace.label
                "
                (click)="toggleFavorite(workspace, $event)"
              >
                <ng-icon
                  [name]="isFavorite(workspace) ? 'HeroStarSolid' : 'HeroStar'"
                ></ng-icon>
              </button>
            </article>
          }
        </div>
      </section>
    }

    <!--Other Workspaces-->
    @if (filteredOtherWorkspaces.length > 0) {
      <section class="workspaces-section">
        <div
          class="workspaces-section-heading flex-it flex-row flex-xcontent-center"
        >
          <h3 class="workspaces-section-title">Other Workspaces</h3>
        </div>
        <div class="workspaces-grid">
          @for (workspace of filteredOtherWorkspaces; track workspace.uuid) {
            <article
              class="workspace-card"
              tabindex="0"
              role="button"
              [attr.aria-label]="'Open ' + workspace.label"
              (click)="activate(workspace)"
              (keydown.enter)="activate(workspace)"
              (keydown.space)="$event.preventDefault(); activate(workspace)"
            >
              <app-workspace-avatar
                [workspace]="workspace"
                [initials]="getInitials(workspace)"
                [fallbackClass]="'workspace-avatar ' + getAvatarClass(workspace)"
              />

              <div class="workspace-card-body">
                <h4
                  class="workspace-card-name"
                  [matTooltip]="
                    workspace.label +
                    (getOwnerEmail(workspace)
                      ? ' — Owner: ' + getOwnerEmail(workspace)
                      : '')
                  "
                >
                  {{ workspace.label }}
                </h4>
                @if (
                  getDatasourceCount(workspace) !== null ||
                  getModelCount(workspace) !== null
                ) {
                  <div class="workspace-card-stats">
                    @if (getDatasourceCount(workspace) !== null) {
                      <span class="workspace-card-stat" matTooltip="Data Sources">
                        <ng-icon name="HeroDocument" class="icon-fw"></ng-icon>
                        {{ getDatasourceCount(workspace) }}
                      </span>
                    }
                    @if (getModelCount(workspace) !== null) {
                      <span class="workspace-card-stat" matTooltip="Models">
                        <ng-icon name="TablerCategory2" class="icon-fw"></ng-icon>
                        {{ getModelCount(workspace) }}
                      </span>
                    }
                  </div>
                }
                @if (getMostRecentApiRequestLabel(workspace); as lastApiRequest) {
                  <p
                    class="workspace-card-recent"
                    [matTooltip]="getMostRecentApiRequestTooltip(workspace)"
                  >
                    <ng-icon name="HeroClock" class="icon-fw"></ng-icon>
                    {{ lastApiRequest }}
                  </p>
                }
              </div>

              <button
                type="button"
                class="favorite-toggle"
                [class.is-favorite]="isFavorite(workspace)"
                [attr.aria-pressed]="isFavorite(workspace)"
                [attr.aria-label]="
                  isFavorite(workspace)
                    ? 'Unpin ' + workspace.label
                    : 'Pin ' + workspace.label
                "
                (click)="toggleFavorite(workspace, $event)"
              >
                <ng-icon
                  [name]="isFavorite(workspace) ? 'HeroStarSolid' : 'HeroStar'"
                ></ng-icon>
              </button>
            </article>
          }
        </div>
      </section>
    }

    <!--No search results-->
    @if (
      searchText &&
      filteredMyWorkspaces.length === 0 &&
      filteredOtherWorkspaces.length === 0
    ) {
      <div class="workspaces-empty-state">
        <ng-icon
          name="HeroSearch"
          class="workspaces-empty-icon"
        ></ng-icon>
        <h3>No workspaces match "{{ searchText }}"</h3>
        <p>Try a different name, or clear your search to see everything.</p>
        <button mat-stroked-button color="primary" (click)="clearSearch()">
          Clear search
        </button>
      </div>
    }
  }

  @if (!loading && loadError) {
    <div class="workspaces-empty-state">
      <ng-icon
        name="HeroExclamation"
        class="workspaces-empty-icon"
      ></ng-icon>
      <h3>Unable to load workspaces</h3>
      <p>Please try again.</p>
    </div>
  }

  @if (!loading && workspaces && workspaces.length === 0) {
    <div class="workspaces-empty-state">
      <ng-icon
        name="HeroBriefcase"
        class="workspaces-empty-icon"
      ></ng-icon>
      <h3>No workspaces yet</h3>
      <p>Create your first workspace to start bringing in data.</p>
      <button mat-raised-button color="primary" (click)="newWorkspace()">
        <div class="add-icon-button">
          <ng-icon name="HeroPlusSolid" class="add-icon"></ng-icon>
          New workspace
        </div>
      </button>
    </div>
  }
</div>

